import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import hourglass from "../assets/take-photo-guidelines.png";
import menGuidelineImage from "../assets/take-photo-guidelines-men.png"
import cloudadd from "../assets/cloud-add.png";
import toast from "react-hot-toast";
import { usePostAnalyzeFaceMutation, usePostUploadImageMutation } from "../store/api.ts"; // Import the mutation hook
import { logEvent } from "../utils/analytics";
import Loader from "../pages/Loader.js";
import { uploadToS3 } from "../utils.js";

const Quiz2 = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [bodyType, setBodyType] = useState("");
  const [stream, setStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [postUploadImage] = usePostUploadImageMutation();
  const [selectedGender, setSelectedGender] = useState(() => location.state?.genderType ?? null);

  // Initialize the mutation hook
  const [analyzeFace] = usePostAnalyzeFaceMutation();

  useEffect(() => {
    if (location.state && location.state.bodyType) {
      setBodyType(location.state.bodyType);
    }

    // Clean up camera stream when component unmounts
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [location, stream]);


  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
        setSelectedFile(file);
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
    }
  };

  const removeSelectedImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const startCamera = async () => {
    try {
      setShowCamera(true);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, // Front camera
        audio: false,
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      toast.error("Could not access the camera");
      console.error("Camera error:", err);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `capture-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(blob));

            // Stop camera stream
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
            setShowCamera(false);
          }
        },
        "image/jpeg",
        0.95
      );
    }
  };


  const handleContinue = async () => {
    if (!selectedFile) return;
    setIsUploading(true);

    try {
      const imageUrl = await uploadToS3(selectedFile, postUploadImage);
      logEvent('Quiz', 'ImageUpload', 'Quiz2_FaceAnalysis');

      const response = await analyzeFace({
        faceAnalysis: {
          image_url: imageUrl,
          body_type: bodyType,
          gender: selectedGender.toLowerCase()
        },
      }).unwrap();

      navigate(`/quiz3`, { state: { faceAnalysis: response, genderType: selectedGender } });
    } catch (error) {
      console.error("Error during upload or analysis:", error);
      toast.error(
        error.data?.message || error.data?.error || "Something went wrong! Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mt-24 mx-auto px-4 md:px-0">
      <h1 className="text-[20px] md:text-[30px] font-bold leading-[28px] md:leading-[32px] tracking-[0%] text-center mx-auto mb-2">
        Upload a Headshot for your Face Shape and Color Analysis
      </h1>
      <p className="text-[14px] md:text-base text-[#797979] text-center mb-4 md:mb-8">
        For the best analysis, follow these guidelines:
      </p>

      <div className="mt-8 md:mt-24 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-[32px] md:w-[1334px] md:h-[488px] md:mx-auto">
        {/* Left side - Example Image with Guidelines */}
        <div className="relative w-full sm:max-w-[250px] md:max-w-[400px] lg:max-w-[710px] h-[270px] sm:h-[350px] md:h-[487px] rounded-[16px] bg-[#EDE3DD] overflow-hidden mx-auto">
          <img
            src={selectedGender === "Male" ? menGuidelineImage : hourglass}
            alt="Example headshot"
            className="w-full h-full object-cover object-center rounded-[16px]"
          />
        </div>

        {/* Right side - Upload Box, Camera, or Preview */}
        {!previewUrl ? (
          showCamera ? (
            <div className="w-full md:w-[592px] md:h-[488px] flex flex-col items-center justify-center bg-black rounded-[18px] overflow-hidden relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />

              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-4">
                <button
                  onClick={removeSelectedImage}
                  className="w-[136px] h-[50px] rounded-[8px] bg-white/80 text-[#292D32] shadow-lg backdrop-blur-md hover:bg-white font-poppins font-medium text-[14px] transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={capturePhoto}
                  className="w-[136px] h-[50px] rounded-[8px] bg-[#22272A]/80 text-white shadow-lg backdrop-blur-md hover:bg-[#22272A] font-poppins font-medium text-[14px] transition-all duration-300"
                >
                  Take Photo
                </button>
              </div>
            </div>
          ) : (
            <div className="w-full md:w-[592px] md:h-[488px] flex flex-col items-center justify-center md:border-[4px] md:border-dashed md:border-[#79797980] md:rounded-[18px] md:p-6 md:bg-white/50">
              <div className="hidden md:block mb-4">
                <img src={cloudadd} alt="Upload icon" className="w-12 h-12" />
              </div>
              <h3 className="hidden md:block font-poppins font-semibold text-[26px] leading-[28px] tracking-[0%] text-center text-[#292D32] mb-4">
                Choose a file or drag & drop it here
              </h3>
              <p className="hidden md:block font-poppins font-medium text-[16px] leading-[100%] tracking-[0%] text-center text-[#797979] mb-8">
                Please upload images in JPEG, JPG or PNG format
              </p>

              <div className="flex flex-row gap-4 w-full md:w-auto mt-16 md:mt-0">
                <button
                  onClick={startCamera}
                  className="w-[136px] md:w-[148px] h-[50px] rounded-[8px] bg-[#22272A] md:bg-[#FFFFFF99] text-white md:text-[#292D32] shadow-[0px_0px_16px_0px_#00000004] backdrop-blur-[36px] hover:bg-opacity-90 font-poppins font-medium text-[14px] leading-[150%] tracking-[0%]"
                >
                  Take Image
                </button>
                <button
                  onClick={handleFileSelect}
                  className="w-[184px] md:w-[202px] h-[50px] rounded-[8px] bg-[#22272A] md:bg-[#FFFFFF99] text-white md:text-[#292D32] shadow-[0px_0px_16px_0px_#00000004] backdrop-blur-[36px] hover:bg-opacity-90 font-poppins font-medium text-[14px] leading-[150%] tracking-[0%]"
                >
                  Upload your photo
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="w-full md:w-[592px] md:h-[488px] flex flex-col items-center md:rounded-[18px] md:bg-white/50 relative overflow-hidden">
            <div className="w-[80%] h-[80%] mt-3">
              <img
                src={previewUrl}
                alt="Uploaded headshot preview"
                className="w-full h-full object-contain rounded-[12px]"
              />
            </div>

            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-4">
              <button
                onClick={removeSelectedImage}
                className="w-[136px] h-[50px] rounded-[8px] bg-white/80 text-[#292D32] shadow-lg backdrop-blur-md hover:bg-white font-poppins font-medium text-[14px] transition-all duration-300"
              >
                Remove
              </button>
              <button
                onClick={handleFileSelect}
                className="w-[136px] h-[50px] rounded-[8px] bg-[#22272A]/80 text-white shadow-lg backdrop-blur-md hover:bg-[#22272A] font-poppins font-medium text-[14px] transition-all duration-300"
              >
                Change Photo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {/* Continue button */}
      <div className="flex mt-12 md:mt-24 justify-center">
        <button
          onClick={handleContinue}
          disabled={!selectedFile || isUploading}
          className={`w-[262px] h-[51px] rounded-[8px] bg-[#22272A] shadow-lg backdrop-blur-[36px] text-white transition-all duration-300 ${
            !selectedFile || isUploading
              ? "opacity-50 cursor-not-allowed"
              : "hover:opacity-90 hover:scale-105"
          }`}
        >
          {isUploading ? "Uploading..." : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default Quiz2;